import { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

const useUnionCouncils = (filters = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    activeToday: 0,
    active: 0
  });

  const isMountedRef = useRef(true);
  const prevFiltersRef = useRef(null);

  const districtFilter = filters?.district || '';
  const tahsilFilter = filters?.tahsil || '';

  useEffect(() => {
    isMountedRef.current = true;

    const filterKey = JSON.stringify({ district: districtFilter, tahsil: tahsilFilter });
    if (prevFiltersRef.current === filterKey) {
      return;
    }
    prevFiltersRef.current = filterKey;

    const fetchData = async () => {
      try {
        const q = query(collection(db, 'UnionCouncils'));
        const querySnapshot = await getDocs(q);

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let records = [];

        querySnapshot.forEach((doc) => {
          const docData = doc.data();

          // Parse lastLogin
          let lastLoginDate = null;
          if (docData.lastLogin) {
            if (docData.lastLogin.toDate) {
              lastLoginDate = docData.lastLogin.toDate();
            } else if (docData.lastLogin.seconds) {
              lastLoginDate = new Date(docData.lastLogin.seconds * 1000);
            } else {
              lastLoginDate = new Date(docData.lastLogin);
            }
          }

          // Check if logged in today
          const loggedInToday = lastLoginDate && lastLoginDate >= todayStart;

          records.push({
            id: doc.id,
            name: docData.name || '',
            email: docData.email || '',
            phone: docData.phone || '',
            district: docData.district || '',
            tahsil: docData.tahsil || '',
            uc: docData.uc || '',
            uid: docData.uid || '',
            active: docData.active || false,
            address: docData.address || '',
            location: docData.location || null,
            lastLogin: docData.lastLogin,
            lastLoginDate: lastLoginDate,
            loggedInToday: loggedInToday,
            updatedAt: docData.updatedAt,
            createdAt: docData.createdAt
          });
        });

        // Apply scope-based filtering
        let filteredRecords = records;
        if (districtFilter) {
          filteredRecords = filteredRecords.filter(r => r.district === districtFilter);
        }
        if (tahsilFilter) {
          filteredRecords = filteredRecords.filter(r => r.tahsil === tahsilFilter);
        }

        // Recalculate stats based on filtered records
        const filteredActiveToday = filteredRecords.filter(r => r.loggedInToday).length;
        const filteredActiveCount = filteredRecords.filter(r => r.active).length;

        if (isMountedRef.current) {
          setData(filteredRecords);
          setStats({
            total: filteredRecords.length,
            activeToday: filteredActiveToday,
            active: filteredActiveCount
          });
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching union councils:', err);
        if (isMountedRef.current) {
          setError(err.message);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [districtFilter, tahsilFilter]);

  return { data, loading, error, stats };
};

export default useUnionCouncils;
